import { useEffect } from "react";

import { Box, Button, Loader, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import TextAreaWithCounter from "~/../../base-components/lib/inputs/TextAreaWithCounter";

import {
  useGetCuratedSpaceQuery,
  usePublishCuratedSpaceMutation,
  useUpdateCuratedSpaceMutation,
} from "../curation.queries.graphql.generated";

type PublishCuratedSpaceModalProps = {
  curatedSpaceId: string;
  onClose: () => void;
};

export const PublishCuratedSpaceModal = (
  props: PublishCuratedSpaceModalProps
) => {
  const { curatedSpaceId, onClose } = props;

  const getCuratedSpaceQueryInfo = useGetCuratedSpaceQuery({
    variables: {
      getCuratedSpaceId: curatedSpaceId,
    },
    fetchPolicy: "network-only",
  });
  const [updateCuratedSpace, updateCuratedSpaceMutationInfo] =
    useUpdateCuratedSpaceMutation();
  const [publishCuratedSpace, publishCuratedSpaceMutationInfo] =
    usePublishCuratedSpaceMutation();

  const isLoading =
    updateCuratedSpaceMutationInfo.loading ||
    publishCuratedSpaceMutationInfo.loading;
  const error =
    updateCuratedSpaceMutationInfo.error ||
    publishCuratedSpaceMutationInfo.error;

  const form = useForm({
    initialValues: {
      name: "",
      description: "",
    },
    transformValues: (values) => {
      return {
        name: values.name.trim(),
        description: values.description.trim(),
      };
    },
    validate: {
      name: (value) => {
        const trimmedValue = value.trim();
        if (trimmedValue.length < 2) {
          return "Name is too short";
        }
      },

      description: (value) => {
        const trimmedValue = value.trim();

        if (trimmedValue.length < 2) {
          return "Description is too short";
        }

        if (trimmedValue.length > 500) {
          return "Description is too long";
        }
      },
    },
  });

  const existingCuratedSpace = getCuratedSpaceQueryInfo.data?.getCuratedSpace;
  useEffect(() => {
    if (existingCuratedSpace?.metadata == null) return;
    form.setValues({
      name: existingCuratedSpace.metadata.name,
      description: existingCuratedSpace.metadata.description,
    });
  }, [existingCuratedSpace]);

  const handleOnSubmit = async () => {
    console.log("Submit", form.values);
    if (getCuratedSpaceQueryInfo.data == null) return;

    const draft = getCuratedSpaceQueryInfo.data.getCuratedSpace?.draft;
    if (draft == null) return;

    let currentEditorVersionObject = JSON.parse("" + draft.editorVersion);

    if (typeof currentEditorVersionObject === "object") {
      currentEditorVersionObject = {
        version: currentEditorVersionObject.version,
        timestamp: new Date().toISOString(),
      };
    } else {
      currentEditorVersionObject = {
        version: 0,
        timestamp: new Date().toISOString(),
      };
    }
    const editorVersion = JSON.stringify(currentEditorVersionObject);

    try {
      const updateResult = await updateCuratedSpace({
        variables: {
          updateCuratedSpaceId: curatedSpaceId,
          data: {
            css: draft.css,
            html: draft.html,
            projectData: draft.projectData ?? "null",
            editorVersion,
            metadata: {
              name: form.values.name,
              description: form.values.description,
            },
          },
        },
      });

      if (updateResult.errors) {
        throw new Error("Step 1");
      }

      const result = await publishCuratedSpace({
        variables: {
          publishCuratedSpaceId: curatedSpaceId,
        },
      });

      if (result.errors) {
        throw new Error("Step 2");
      }

      console.log("Result", result);
      onClose();
    } catch (error) {
      console.error("Error publishing curated space", error);
    }
  };

  return (
    <Box>
      <Stack>
        <Text c={"dimmed"} size={"sm"}>
          You are about to publish{" "}
          <Text span inherit>{`highlight.xyz/curated/${curatedSpaceId}`}</Text>.
          You can change the name and the description before you do.
        </Text>

        {getCuratedSpaceQueryInfo.loading ? (
          <Stack spacing={4} align="center" py={"xl"}>
            <Loader />
            <Text size={"sm"} c={"dimmed"}>
              Loading latest curated space data.
            </Text>
          </Stack>
        ) : (
          <Box component="form" onSubmit={form.onSubmit(handleOnSubmit)}>
            <Stack>
              {/* <TextInput
                label="Slug"
                value={curatedSpaceId}
                icon={
                  <Text size={"sm"} c={"dimmed"} w={"100%"} pl={"md"}>
                    highlight.xyz/curated/
                  </Text>
                }
                iconWidth={164}
                description="Slug is used in the URL."
                descriptionProps={{ size: "sm", c: "dimmed" }}
                readOnly
                disabled
              /> */}

              <TextInput
                label="Name"
                {...form.getInputProps("name")}
                description="Name of the curated space. Will be shown in the browser tab and metadata."
                descriptionProps={{ size: "sm", c: "dimmed" }}
              />

              <TextAreaWithCounter
                characterCounter
                maxLength={500}
                minRows={3}
                maxRows={5}
                autosize
                label="Description"
                description="Short description of the curated space. Will be used in the metadata."
                descriptionProps={{ size: "sm", c: "dimmed" }}
                {...form.getInputProps("description")}
              />

              {error?.message && (
                <Text c={"red.5"} fw={500}>
                  {error.message}
                </Text>
              )}

              <Button type="submit" loading={isLoading}>
                Publish
              </Button>
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
